/**
 * The Founding view's feed, straight from pv-auth (FOUNDING_MEMBERS_2026-10-17.md):
 *   GET   /provider/admin/founding           every founding member with live checks, streak, open feedback, plan, next action
 *   POST  /provider/admin/founding           add one by email: grants the founding period (comp), tags and cards them in GHL
 *   PATCH /provider/admin/founding/:userId   status, call, next action, weekly note sent, name on the founders page
 * Shapes mirror src/services/foundingMember.service.ts and foundingMember.pure.ts
 * in pv-auth-service. A null is a thing the server could not measure (a streak
 * with no checks) and the page prints the words for it.
 */

export const FOUNDING_STATUSES = ['invited', 'accepted', 'active', 'paused', 'left'] as const;
export type FoundingStatus = (typeof FOUNDING_STATUSES)[number];
export const STATUS_LABEL: Record<FoundingStatus, string> = { invited: 'Invited', accepted: 'Said yes', active: 'Using it', paused: 'Paused', left: 'Left' };

export const SOURCES = ['two_checks', 'wrote', 'primal_trust', 'paying', 'recent', 'other'] as const;
export const SOURCE_LABEL: Record<string, string> = { two_checks: 'Two or more checks', wrote: 'Wrote to us', primal_trust: 'Primal Trust', paying: 'Paying', recent: 'Recent sign-up', other: 'Other' };

export type DueState = 'overdue' | 'today' | 'soon' | 'later' | 'none';
export const DUE_LABEL: Record<DueState, string> = { overdue: 'Overdue', today: 'Today', soon: 'This week', later: 'Later', none: 'Nothing due' };

export interface OpenFeedbackItem { kind: 'typed' | 'help' | 'ticket' | 'inbound'; at: string; text: string }
export const FEEDBACK_KIND_LABEL: Record<OpenFeedbackItem['kind'], string> = { typed: 'typed in the product', help: 'help request', ticket: 'support ticket', inbound: 'wrote to us' };

export interface FoundingRow {
  userId: string;
  email: string;
  name: string;
  firstName: string;
  timezone: string | null;
  phone: boolean;
  verified: boolean;
  status: FoundingStatus;
  source: string | null;
  invitedAt: string;
  acceptedAt: string | null;
  callAt: string | null;
  callDoneAt: string | null;
  nextAction: string | null;
  nextActionDueAt: string | null;
  nextActionDue: DueState;
  lastWeeklyNoteAt: string | null;
  namePublicOkAt: string | null;
  lastCheckAt: string | null;
  streak: number | null;
  checks: number;
  checkDays: number;
  openFeedbackCount: number;
  openFeedback: OpenFeedbackItem[];
  plan: { kind: 'comp' | 'paid' | 'free'; label: string; endsAt: string | null };
  ghl: { contactUrl: string | null; opportunityId: string | null };
}

export interface FoundingSummary { total: number; byStatus: Record<FoundingStatus, number>; owed: number; withOpenFeedback: number }

export interface FoundingList {
  generatedAt: string;
  statuses: readonly FoundingStatus[];
  statusLabel: Record<FoundingStatus, string>;
  sources: readonly string[];
  pipeline: { configured: boolean; name: string; reason: string | null };
  summary: FoundingSummary;
  rows: FoundingRow[];
}

export interface AddFoundingResult {
  userId: string;
  comp: { granted: boolean; days: number; reason: string | null };
  ghl: { contactId: string | null; opportunityId: string | null; tagged: boolean };
}

export interface UpdateFoundingResult { userId: string; status: FoundingStatus; ghl: { opportunityId: string | null; tagged: boolean } | null }

/* ── pure helpers ───────────────────────────────────────────────────────── */

const DAY_MS = 24 * 60 * 60 * 1000;

/** "today", "yesterday", "3 days ago", "never": for the last check column. */
export const daysAgoWords = (iso: string | null, now: Date = new Date()): string => {
  if (!iso) return 'never';
  const d = Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d} days ago`;
};

/** The streak column: null is "no checks yet", 0 is "none", n is "n days". Never a dash for a number. */
export const streakWords = (streak: number | null): string => (streak === null ? 'no checks yet' : streak === 0 ? 'none' : streak === 1 ? '1 day' : `${streak} days`);

/** What the add form reports back, in one line. */
export const addResultWords = (r: AddFoundingResult): string => {
  const comp = r.comp.granted ? `${r.comp.days} days free granted` : r.comp.reason ? `no founding period (${r.comp.reason})` : 'no founding period';
  const ghl = r.ghl.opportunityId ? 'GHL card made' : r.ghl.tagged ? 'GHL tagged, no card (pipeline missing)' : r.ghl.contactId ? 'GHL contact found, tag and card failed' : 'no GHL contact';
  return `Added. ${comp}; ${ghl}.`;
};

/** The one line a row shows under the name: status, since when, the call. */
export const rowLine = (r: FoundingRow): string => {
  const bits = [`${STATUS_LABEL[r.status]} since ${(r.status === 'invited' ? r.invitedAt : r.acceptedAt ?? r.invitedAt).slice(0, 10)}`];
  if (r.callDoneAt) bits.push(`call done ${r.callDoneAt.slice(0, 10)}`);
  else if (r.callAt) bits.push(`call ${r.callAt.slice(0, 10)}`);
  else bits.push('no call booked');
  if (r.lastWeeklyNoteAt) bits.push(`weekly note ${r.lastWeeklyNoteAt.slice(0, 10)}`);
  if (r.namePublicOkAt) bits.push('name OK for the founders page');
  return bits.join(' · ');
};

/** A YYYY-MM-DD for a date input, or empty. */
export const dateInputValue = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

/* ── fetchers ───────────────────────────────────────────────────────────── */

export async function fetchFounding(): Promise<FoundingList> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/founding', true)) as FoundingList;
}

export async function addFounding(input: { email: string; source?: string | null; compDays?: number | null; nextAction?: string | null; nextActionDueAt?: string | null }): Promise<AddFoundingResult> {
  const { apiPost } = await import('@/services/base');
  return (await apiPost('/provider/admin/founding', input, true)) as AddFoundingResult;
}

export async function updateFounding(userId: string, patch: Partial<{ status: FoundingStatus; source: string | null; callAt: string | null; callDoneAt: string | null; nextAction: string | null; nextActionDueAt: string | null; weeklyNoteSent: boolean; namePublicOk: boolean }>): Promise<UpdateFoundingResult> {
  const { apiPatch } = await import('@/services/base');
  return (await apiPatch(`/provider/admin/founding/${encodeURIComponent(userId)}`, patch, true)) as UpdateFoundingResult;
}
